import { cn } from "@/lib/utils";
import { RiDeleteBin6Line } from "@remixicon/react";
import React, { useState } from "react";
import { useSwipeable } from "react-swipeable";

// Width of the revealed delete action, in px. The foreground translates by at
// most this amount so the action sits fully behind the row when open.
const ACTION_WIDTH = 88;
// Past this much horizontal travel a swipe snaps the row open/closed.
const SNAP_THRESHOLD = ACTION_WIDTH / 2;

interface SwipeableRowProps {
  /** Fired when the revealed delete action is tapped. */
  onDelete: () => void;
  /** Test id for the delete action button. */
  deleteTestId?: string;
  /** Accessible label for the delete action. */
  deleteLabel?: string;
  children: React.ReactNode;
}

/**
 * iOS-style swipe-to-reveal row: swipe the content left to expose a red delete
 * action behind it. Tapping the action fires `onDelete`; swiping right, or
 * tapping the content while open, closes the row. Vertical scrolling is
 * preserved — only horizontal swipes engage.
 *
 * Presentational only: it knows nothing about transactions or permissions.
 */
const SwipeableRow = ({
  onDelete,
  deleteTestId,
  deleteLabel = "Delete",
  children,
}: SwipeableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // Live finger-follow offset while swiping; null when not actively swiping.
  const [dragX, setDragX] = useState<number | null>(null);

  const handlers = useSwipeable({
    onSwiping: (e) => {
      // Only react to horizontal intent; let vertical scroll through.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      const base = isOpen ? -ACTION_WIDTH : 0;
      const next = Math.min(0, Math.max(-ACTION_WIDTH, base + e.deltaX));
      setDragX(next);
    },
    onSwipedLeft: () => {
      setIsOpen(true);
      setDragX(null);
    },
    onSwipedRight: () => {
      setIsOpen(false);
      setDragX(null);
    },
    onSwiped: () => {
      // Fallback for swipes that don't cross the directional threshold:
      // snap based on how far the row was dragged.
      setDragX((current) => {
        if (current !== null) {
          setIsOpen(current <= -SNAP_THRESHOLD);
        }
        return null;
      });
    },
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 10,
  });

  const translateX = dragX ?? (isOpen ? -ACTION_WIDTH : 0);

  const close = () => setIsOpen(false);

  return (
    <div {...handlers} className="w-full overflow-hidden rounded-lg">
      {/* The card and the delete action are siblings in a flex row. When
          closed (translateX 0) the action sits just past the right edge and is
          clipped away, so nothing bleeds through the translucent card. Swiping
          left slides both so the action comes into view. */}
      <div
        className={cn(
          "flex w-full touch-pan-y",
          dragX === null && "transition-transform duration-200 ease-out"
        )}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        <div
          // While open, a tap closes the row instead of activating its content
          // (e.g. navigating). Capturing the click also swallows the synthetic
          // click that follows a swipe gesture.
          onClickCapture={(e) => {
            if (isOpen || dragX !== null) {
              e.stopPropagation();
              e.preventDefault();
              close();
            }
          }}
          className="w-full shrink-0"
        >
          {children}
        </div>

        <button
          type="button"
          data-testid={deleteTestId}
          aria-label={deleteLabel}
          onClick={(e) => {
            e.stopPropagation();
            close();
            onDelete();
          }}
          className="flex shrink-0 items-center justify-center bg-destructive text-white"
          style={{ width: ACTION_WIDTH }}
        >
          <RiDeleteBin6Line size={20} />
        </button>
      </div>
    </div>
  );
};

export default SwipeableRow;
